import { fetchRetry429 } from "../../../utils/sessionRetry";
import { useState, useEffect, useRef, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { SearchError } from "../../../errors/sentryErrors";
import { API_URL } from "../../../config";

const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return "🌍";
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const extractCountryCode = (addressComponents) => {
  if (!addressComponents) return null;
  const country = addressComponents.find(c =>
    c.types?.includes("country")
  );
  return country?.shortText || null;
};

const extractLocationParts = (addressComponents) => {
  if (!addressComponents) return { city: null, state: null, country: null };

  let city = null;
  let state = null;
  let country = null;

  for (const component of addressComponents) {
    const types = component.types || [];
    if (types.includes("locality")) {
      city = component.longText;
    } else if (types.includes("administrative_area_level_1")) {
      state = component.longText;
    } else if (types.includes("country")) {
      country = component.longText;
    }
  }

  return { city, state, country };
};

const getDisplayType = (types) => {
  if (!types) return "place";
  if (types.includes("locality") || types.includes("city")) return "city";
  if (types.includes("administrative_area_level_1")) return "state";
  if (types.includes("country")) return "country";
  if (types.includes("subcontinent") || types.includes("region")) return "region";
  if (types.includes("airport")) return "airport";
  if (types.includes("tourist_attraction")) return "attraction";
  return "place";
};

const LocationAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search for a city...",
  icon: Icon = null,
  filterType = "city"
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  const [nestedRegion, setNestedRegion] = useState(null);
  const [nestedCities, setNestedCities] = useState([]);
  const [isFetchingNested, setIsFetchingNested] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setSessionToken(crypto.randomUUID());
  }, []);

  const fetchFromGooglePlaces = async (query) => {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error("Google Places API key not configured. Set VITE_GOOGLE_PLACES_API_KEY in .env");
      return [];
    }

    try {
      let includedPrimaryTypes = [];
      if (filterType === "city") {
        includedPrimaryTypes = ["locality", "administrative_area_level_3", "postal_town"];
      } else if (filterType === "country") {
        includedPrimaryTypes = ["country"];
      }

      const requestBody = {
        input: query,
        languageCode: "en",
      };

      if (includedPrimaryTypes.length > 0) {
        requestBody.includedPrimaryTypes = includedPrimaryTypes;
      }

      if (sessionToken) {
        requestBody.sessionToken = sessionToken;
      }

      const response = await fetch(
        "https://places.googleapis.com/v1/places:autocomplete",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Google Places API error:", errorData);
        return [];
      }

      const data = await response.json();

      if (data.suggestions && data.suggestions.length > 0) {
        const detailedResults = await Promise.all(
          data.suggestions.slice(0, 6).map(async (suggestion) => {
            const place = suggestion.placePrediction;
            if (!place) return null;

            const detailsResponse = await fetch(
              `https://places.googleapis.com/v1/places/${place.placeId}?fields=id,displayName,formattedAddress,addressComponents,location,types`,
              {
                headers: {
                  "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
                },
              }
            );

            if (!detailsResponse.ok) return null;

            const details = await detailsResponse.json();
            const locationParts = extractLocationParts(details.addressComponents);
            const countryCode = extractCountryCode(details.addressComponents);

            return {
              id: place.placeId,
              placeId: place.placeId,
              name: locationParts.city || details.displayName?.text || place.structuredFormat?.mainText?.text || "Unknown",
              city: locationParts.city || details.displayName?.text,
              state: locationParts.state,
              country: locationParts.country,
              countryCode: countryCode,
              fullName: details.formattedAddress || place.text?.text,
              type: getDisplayType(details.types),
              lat: details.location?.latitude,
              lon: details.location?.longitude,
              formattedAddress: details.formattedAddress,
            };
          })
        );

        return detailedResults.filter(Boolean);
      }
      return [];
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "search");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new SearchError(error instanceof Error ? error.message : String(error)));
      });
      console.error("Google Places API error:", error);
      return [];
    }
  };

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await fetchFromGooglePlaces(query);
      setSuggestions(results);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "search");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new SearchError(error instanceof Error ? error.message : String(error)));
      });
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [filterType, sessionToken]);

  useEffect(() => {
    if (!value) {
      setSelectedLocation(null);
    }
  }, [value]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (value && !selectedLocation) {
        fetchSuggestions(value);
      }
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [value, fetchSuggestions, selectedLocation]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const completeSelection = (locationData) => {
    setSelectedLocation(locationData);
    onChange(locationData.name);
    onSelect(locationData);
    setSuggestions([]);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setSessionToken(crypto.randomUUID());

    setNestedRegion(null);
    setNestedCities([]);
  };

  const handleSelect = async (suggestion) => {
    if (["state", "country", "region", "subcontinent"].includes(suggestion.type)) {
      setNestedRegion(suggestion);
      setNestedCities([]);
      setIsFetchingNested(true);
      setHighlightedIndex(-1); // Reset highlight index for nested cities list navigation

      try {
        const res = await fetchRetry429(() => fetch(`${API_URL}/api/route/suggest-cities?region=${encodeURIComponent(suggestion.name)}&country=${encodeURIComponent(suggestion.country || "")}`));
        const data = await res.json();

        if (data.cities && data.cities.length > 0) {
          setNestedCities(data.cities);
        } else {
          completeSelection(suggestion);
        }
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "search");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new SearchError(err instanceof Error ? err.message : String(err)));
        });
        console.error(err);
        completeSelection(suggestion);
      } finally {
        setIsFetchingNested(false);
      }
    } else {
      completeSelection(suggestion);
    }
  };

  const handleClear = () => {
    onChange("");
    onSelect(null);
    setSelectedLocation(null);
    setSuggestions([]);
    setNestedRegion(null);
    setNestedCities([]);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedLocation(null);
    setNestedRegion(null);
    setNestedCities([]);
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    if (nestedRegion) {
      const totalItems = 1 + nestedCities.length;
      if (totalItems <= 1) return; // Cities not loaded yet

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev < totalItems - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : totalItems - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex === 0) {
            completeSelection(nestedRegion);
          } else if (highlightedIndex > 0 && highlightedIndex < totalItems) {
            const city = nestedCities[highlightedIndex - 1];
            completeSelection({
              id: `nested-${city.name}-${Date.now()}`,
              placeId: `nested-${city.name}-${Date.now()}`,
              name: city.name,
              city: city.name,
              country: city.country,
              countryCode: nestedRegion.countryCode,
              type: city.type || "city",
              lat: city.lat,
              lon: city.lon,
              formattedAddress: `${city.name}, ${city.country}`
            });
          }
          break;
        case 'Escape':
          setShowDropdown(false);
          setHighlightedIndex(-1);
          break;
      }
    } else {
      if (suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSelect(suggestions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setShowDropdown(false);
          setHighlightedIndex(-1);
          break;
      }
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative flex items-center">
        {Icon && (
          <span className="absolute left-3 text-gray-400 pointer-events-none">
            <Icon />
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            setShowDropdown(true);
            if (value && !selectedLocation && suggestions.length === 0) {
              fetchSuggestions(value);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full ${Icon ? 'pl-11' : 'pl-4'} pr-10 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-400 transition-all duration-200 bg-white hover:border-gray-300`}
          autoComplete="off"
        />

        {isLoading && (
          <span className="absolute right-3 pointer-events-none">
            <svg className="animate-spin h-5 w-5 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
        )}

        {value && !isLoading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {selectedLocation && !isLoading && (
          <span className="absolute right-10 text-teal-500 pointer-events-none">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </span>
        )}
      </div>

      {showDropdown && (
        <div
          className={`absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden transition-all duration-200 ${nestedRegion || suggestions.length > 0 || isLoading ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
            }`}
        >
          {nestedRegion ? (
            <div className="py-2">
              <div
                onClick={() => completeSelection(nestedRegion)}
                onMouseEnter={() => setHighlightedIndex(0)}
                className={`px-4 py-3 cursor-pointer flex items-center gap-3 border-b border-gray-100 transition-colors duration-100 ${highlightedIndex === 0 ? 'bg-teal-50' : 'hover:bg-gray-50'
                  }`}
              >
                <span className="text-gray-400 flex-shrink-0 w-5 h-5 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-gray-900 text-[15px]">{nestedRegion.name}</span>
                    <span className="text-xs text-gray-500 font-normal">
                      · {nestedRegion.type === 'country' ? 'Country' : `${nestedRegion.type === 'state' || nestedRegion.type === 'administrative_area_level_1' ? 'State' : 'Region'}${nestedRegion.country ? ` in ${nestedRegion.country}` : ''}`}
                    </span>
                  </div>
                </div>
              </div>

              {isFetchingNested ? (
                <div className="px-4 py-6 text-center">
                  <svg className="animate-spin h-6 w-6 text-teal-500 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm text-gray-500">Finding popular cities...</p>
                </div>
              ) : (
                <ul className="py-1 relative">
                  {nestedCities.map((city, idx) => {
                    const isLast = idx === nestedCities.length - 1;
                    const itemIdx = idx + 1;
                    return (
                      <li
                        key={city.name}
                        onClick={() => completeSelection({
                          id: `nested-${city.name}-${Date.now()}`,
                          placeId: `nested-${city.name}-${Date.now()}`,
                          name: city.name,
                          city: city.name,
                          country: city.country,
                          countryCode: nestedRegion.countryCode,
                          type: city.type || "city",
                          lat: city.lat,
                          lon: city.lon,
                          formattedAddress: `${city.name}, ${city.country}`
                        })}
                        onMouseEnter={() => setHighlightedIndex(itemIdx)}
                        className={`relative pl-12 pr-4 py-2.5 cursor-pointer flex flex-col justify-center min-h-[52px] transition-colors duration-100 ${highlightedIndex === itemIdx ? 'bg-teal-50' : 'hover:bg-gray-50'
                          }`}
                      >
                        {/* Trunk line segment */}
                        <div className={`absolute left-[26px] w-0.5 bg-gray-200 ${isLast ? 'top-0 h-[18px]' : 'top-0 bottom-0'}`} />

                        {/* Branch line segment */}
                        <div className="absolute left-[26px] top-[18px] w-[14px] h-0.5 bg-gray-200" />

                        {/* Arrow head */}
                        <span className="absolute left-[38px] top-[11px] text-gray-300 text-[10px] pointer-events-none select-none">→</span>

                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-800 text-[14px] leading-tight">{city.name}</span>
                          {city.description && (
                            <span className="text-xs text-gray-400 mt-0.5 font-normal leading-normal">{city.description}</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
            <>
              {isLoading && suggestions.length === 0 && (
                <div className="px-4 py-8 text-center">
                  <svg className="animate-spin h-6 w-6 text-teal-500 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm text-gray-500">Searching places...</p>
                </div>
              )}

              {suggestions.length > 0 && (
                <ul className="max-h-72 overflow-auto py-1">
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={suggestion.id}
                      onClick={() => handleSelect(suggestion)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`px-4 py-3 cursor-pointer transition-colors duration-100 ${highlightedIndex === index
                          ? 'bg-teal-50'
                          : 'hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl flex-shrink-0">
                          {getCountryFlag(suggestion.countryCode)}
                        </span>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">
                              {suggestion.name}
                            </span>
                            <span className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded ${suggestion.type === 'city' ? 'bg-blue-100 text-blue-700' :
                                suggestion.type === 'town' ? 'bg-green-100 text-green-700' :
                                  suggestion.type === 'country' ? 'bg-purple-100 text-purple-700' :
                                    'bg-gray-100 text-gray-600'
                              }`}>
                              {suggestion.type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate">
                            {[suggestion.state, suggestion.country].filter(Boolean).join(', ')}
                          </p>
                        </div>

                        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-opacity ${highlightedIndex === index ? 'opacity-100' : 'opacity-0'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {suggestions.length > 0 && (
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-400 flex items-center gap-2">
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">↑</kbd>
                      <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">↓</kbd>
                      Navigate
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">↵</kbd>
                      Select
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">Esc</kbd>
                      Close
                    </span>
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showDropdown && !isLoading && !nestedRegion && value && value.length >= 2 && suggestions.length === 0 && !selectedLocation && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl p-6 text-center">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm text-gray-500">No places found for "{value}"</p>
          <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
